<ion-content style="--padding-top: 48px; --background: #f4f5f8;">

    <div class="w-full max-w-6xl mx-auto p-4">

        <!-- Credits Section -->
        <div *ngIf="!loading" class="mb-6 p-5 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-2xl border border-amber-200/60 shadow-sm">
            <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <!-- Credits Info -->
                <div class="flex items-center gap-4">
                    <div class="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-200">
                        <i class="fa-solid fa-coins text-white text-2xl"></i>
                    </div>
                    <div>
                        <div class="flex items-baseline gap-2">
                            <span class="text-3xl font-bold text-gray-900">{{ userCredits | number:'1.0-2' }}</span>
                            <span class="text-sm font-medium text-gray-500">{{ 'CREDITS' | translate }}</span>
                        </div>
                        <div class="flex items-center gap-1.5 mt-0.5">
                            <p class="text-sm text-gray-500">
                                <i class="fa-solid fa-globe text-amber-500 mr-1"></i>
                                {{ 'CREDITS_GLOBAL_EXPLANATION' | translate }}
                            </p>
                            <button (click)="openCreditsInfoModal()"
                                    class="w-5 h-5 flex items-center justify-center rounded-full bg-amber-200 hover:bg-amber-300 transition-colors cursor-pointer"
                                    title="{{ 'CREDITS_LEARN_MORE' | translate }}">
                                <i class="fa-solid fa-question text-amber-700 text-xs"></i>
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Actions -->
                <div class="flex items-center gap-2">
                    <!-- Auto-recharge Button -->
                    <button (click)="openAutoRechargeModal()"
                            title="{{ 'AUTO_RECHARGE_TITLE' | translate }}"
                            class="flex items-center gap-2 px-4 py-2.5 bg-white/70 hover:bg-white text-amber-700 font-medium rounded-xl border border-amber-300 transition-all shadow-sm hover:shadow">
                        <i class="fa-solid fa-bolt"></i>
                        <span class="hidden sm:inline">{{ 'AUTO_RECHARGE_BUTTON' | translate }}</span>
                    </button>

                    <!-- Buy Credits Button -->
                    <button (click)="openBuyCreditsModal()"
                            class="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-xl transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5">
                        <i class="fa-solid fa-plus"></i>
                        <span>{{ 'BUY_MORE_CREDITS' | translate }}</span>
                    </button>
                </div>
            </div>
        </div>

        <!-- Credit Usage Chart -->
        <app-credit-usage-chart *ngIf="!loading && userData" [userData]="userData"></app-credit-usage-chart>

        <!-- Agent API & MCP Banner -->
        <a *ngIf="!loading" routerLink="/vcaas"
           class="group relative flex items-center gap-3 mt-4 mb-6 px-4 py-3 rounded-xl text-white no-underline overflow-hidden shadow-md hover:shadow-lg transition-all"
           style="background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #ec4899 100%);">
            <div class="flex-shrink-0 w-9 h-9 rounded-lg bg-white/20 flex items-center justify-center">
                <i class="fa-solid fa-code text-base"></i>
            </div>
            <div class="flex-1 min-w-0">
                <div class="text-sm font-bold leading-tight">Agent API & MCP</div>
                <div class="text-xs opacity-90 leading-tight">Build, deploy and manage projects programmatically</div>
            </div>
            <i class="fa-solid fa-arrow-right text-sm opacity-90 group-hover:translate-x-0.5 transition-transform"></i>
        </a>

        <!-- Credits Skeleton when loading -->
        <div *ngIf="loading" class="mb-6 p-5 bg-gradient-to-r from-amber-50 via-orange-50 to-yellow-50 rounded-2xl border border-amber-200/60">
            <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div class="flex items-center gap-4">
                    <ion-skeleton-text animated style="width: 56px; height: 56px; border-radius: 12px;"></ion-skeleton-text>
                    <div>
                        <ion-skeleton-text animated style="width: 80px; height: 28px;"></ion-skeleton-text>
                        <ion-skeleton-text animated style="width: 150px; height: 14px; margin-top: 4px;"></ion-skeleton-text>
                    </div>
                </div>
                <ion-skeleton-text animated style="width: 150px; height: 40px; border-radius: 12px;"></ion-skeleton-text>
            </div>
        </div>

        <!-- Title + Controls -->
        <div class="mb-4">
            <h1 class="text-base font-semibold text-slate-900 mb-4">Your projects</h1>

            <!-- Search, Filter, Sort bar -->
            <div class="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
                <!-- Search -->
                <div class="relative flex-1 w-full sm:max-w-xs">
                    <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"></i>
                    <input type="text"
                           [(ngModel)]="searchQuery"
                           (ngModelChange)="onSearchChange()"
                           placeholder="Search by projectId..."
                           class="w-full h-9 pl-9 pr-3 text-[13px] border border-gray-300 rounded-lg outline-none focus:border-gray-400 transition-colors bg-white">
                </div>

                <!-- Filter by plan (dropdown, only existing plans) -->
                <select [(ngModel)]="filterPlan"
                        (ngModelChange)="onFilterChange()"
                        class="plan-filter-select">
                    <option value="all">All plans</option>
                    <option *ngFor="let p of availablePlans" [value]="p.value">{{p.label}} ({{p.count}})</option>
                </select>

                <!-- Results count -->
                <span class="text-xs text-gray-400 whitespace-nowrap" *ngIf="!loading">
                    {{filteredInstances.length}} project{{filteredInstances.length !== 1 ? 's' : ''}}
                </span>

                <!-- Spacer -->
                <div class="flex-1 hidden sm:block"></div>

                <!-- New project button -->
                <button (click)="openCreateInstanceModal({plan:'free'})"
                        class="create-project-inline-btn">
                    <i class="fa-solid fa-plus"></i>
                    <span>New project</span>
                </button>
            </div>
        </div>
        
        <!-- Skeleton rows when loading -->
        <div *ngIf="loading" class="list-view-container">
            <div class="list-view-table">
                <div class="list-header">
                    <div class="list-col col-name">Project</div>
                    <div class="list-col col-plan">Plan</div>
                    <div class="list-col col-date">Created</div>
                    <div class="list-col col-actions">Actions</div>
                </div>
                <div *ngFor="let item of [1,2,3]" class="list-row">
                    <div class="list-col col-name" style="gap: 10px;">
                        <ion-skeleton-text animated style="width: 22px; height: 22px; border-radius: 4px; flex-shrink: 0;"></ion-skeleton-text>
                        <ion-skeleton-text animated style="width: 140px; height: 14px;"></ion-skeleton-text>
                    </div>
                    <div class="list-col col-plan">
                        <ion-skeleton-text animated style="width: 60px; height: 20px; border-radius: 6px;"></ion-skeleton-text>
                    </div>
                    <div class="list-col col-date">
                        <ion-skeleton-text animated style="width: 120px; height: 14px;"></ion-skeleton-text>
                    </div>
                    <div class="list-col col-actions" style="gap: 6px;">
                        <ion-skeleton-text animated style="width: 60px; height: 28px; border-radius: 7px;"></ion-skeleton-text>
                        <ion-skeleton-text animated style="width: 120px; height: 28px; border-radius: 7px;"></ion-skeleton-text>
                    </div>
                </div>
            </div>
        </div>

        <!-- ═══════════ LIST VIEW ═══════════ -->
        <div *ngIf="!loading" class="list-view-container">
            <div class="list-view-table">
                <div class="list-header">
                    <div class="list-col col-name sortable-col" (click)="sortBy('name')">
                        Project
                        <i *ngIf="sortField === 'name'" class="fa-solid sort-icon"
                           [class.fa-caret-up]="sortDirection === 'asc'"
                           [class.fa-caret-down]="sortDirection === 'desc'"></i>
                    </div>
                    <div class="list-col col-plan sortable-col" (click)="sortBy('plan')">
                        Plan
                        <i *ngIf="sortField === 'plan'" class="fa-solid sort-icon"
                           [class.fa-caret-up]="sortDirection === 'asc'"
                           [class.fa-caret-down]="sortDirection === 'desc'"></i>
                    </div>
                    <div class="list-col col-date sortable-col" (click)="sortBy('date')">
                        Created
                        <i *ngIf="sortField === 'date'" class="fa-solid sort-icon"
                           [class.fa-caret-up]="sortDirection === 'asc'"
                           [class.fa-caret-down]="sortDirection === 'desc'"></i>
                    </div>
                    <div class="list-col col-actions">Actions</div>
                </div>
                <div *ngFor="let instance of paginatedInstances; let i = index"
                     class="list-row">
                    <div class="list-col col-name">
                        <img src="assets/totalum-logo-solo-recortado.png" alt="Totalum" class="list-logo">
                        <div class="list-project-info">
                            <span class="list-project-name">{{ instance?.organizationId ?? 'Totalum' }}</span>
                            <span *ngIf="instance?.projectDescription" class="list-project-desc" [title]="instance.projectDescription">{{ instance.projectDescription }}</span>
                        </div>
                    </div>
                    <div class="list-col col-plan">
                        <span class="plan-badge" [ngClass]="getPlanClass(instance)">
                            {{instance.plan === 'free' ? ('FREE_PLAN' | translate) :
                              instance.plan === 'starter' ? 'Starter' :
                              instance.plan === 'shared' ? ('BASIC_PLAN' | translate) :
                              instance.plan === 'profesional' ? ('PROFESSIONAL_PLAN' | translate) :
                              instance.plan === 'enterprise' ? ('ENTERPRISE_PLAN' | translate) :
                              instance.plan === 'api' ? 'API' :
                              ('SERVER_PLAN' | translate)}}
                        </span>
                    </div>
                    <div class="list-col col-date">
                        <span class="text-slate-600 text-[13px]">{{(instance?.createdAt | date: 'dd/MM/yyyy, HH:mm') || '-'}}</span>
                    </div>
                    <!-- a new-Totalum (v2) project: listed, managed only through the API (it has no workspace here) -->
                    <div *ngIf="instance?.engineVersion === 'v2'" class="list-col col-actions">
                        <span class="text-slate-500 text-[12px]" title="Created on the new Totalum: manage it through the API">
                            <i class="fa-solid fa-code mr-1"></i>New Totalum · API only
                        </span>
                    </div>
                    <div *ngIf="instance?.engineVersion !== 'v2'" class="list-col col-actions">
                        <a [href]="getInstanceUrl(instance)" target="_blank" class="list-action-btn list-action-open">
                            <i class="fa-solid fa-arrow-up-right-from-square"></i>
                            <span>Open</span>
                        </a>
                        <button (click)="onOpenOtherProfile(instance)"
                                class="list-action-btn list-action-other-profile">
                            <i class="fa-solid fa-user-group"></i>
                            <span>Open in other profile</span>
                        </button>
                        <button *ngIf="!instance.serverId && instance.plan !== 'enterprise' && instance.plan !== 'api'"
                                (click)="onUpgrade(instance)"
                                class="list-action-btn list-action-upgrade">
                            <i class="fa-solid fa-arrow-up"></i>
                            <span>Upgrade</span>
                        </button>
                        <div class="relative">
                            <button (click)="togglePopover(i)" class="popover-trigger-btn list-action-btn list-action-more">
                                <i class="fa-solid fa-ellipsis"></i>
                            </button>
                            <div *ngIf="activePopover === i"
                                 (click)="togglePopover(-1)"
                                 class="absolute right-0 top-9 bg-white rounded-lg z-50 min-w-[150px] max-w-[200px]"
                                 style="box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);">
                                <button *ngIf="instance?.plan !== 'api'"
                                        (click)="onChangePlan(instance)"
                                        class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg">
                                    <i class="fa-solid fa-arrow-right-arrow-left mr-2"></i>
                                    {{ 'CHANGE_PLAN' | translate }}
                                </button>
                                <button (click)="onToggleApiAccess(instance)"
                                        class="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 transition-colors rounded-lg">
                                    <i class="fa-solid" [ngClass]="isApiAccessEnabled(instance) ? 'fa-lock' : 'fa-unlock'" style="margin-right: 8px;"></i>
                                    {{ isApiAccessEnabled(instance) ? 'Disable API access' : 'Enable API access' }}
                                </button>
                                <button (click)="onDeleteInstance(instance)"
                                    *ngIf="instance?.plan === 'free' || instance?.plan === 'api'"
                                        class="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-50 transition-colors rounded-lg">
                                    <i class="fa-solid fa-trash-can mr-2"></i>
                                    {{ 'DELETE_PROJECT' | translate }}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </div>

        <!-- Pagination -->
        <div *ngIf="!loading && totalPages > 1" class="mt-6 flex items-center justify-center gap-2">
            <button (click)="goToPage(currentPage - 1)"
                    [disabled]="currentPage === 1"
                    class="h-8 w-8 rounded-md border text-[13px] transition-colors flex items-center justify-center"
                    [ngClass]="currentPage === 1 ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer'">
                <i class="fa-solid fa-chevron-left text-xs"></i>
            </button>

            <span class="text-[13px] text-gray-600 px-2">
                Page {{currentPage}} of {{totalPages}}
            </span>

            <button (click)="goToPage(currentPage + 1)"
                    [disabled]="currentPage === totalPages"
                    class="h-8 w-8 rounded-md border text-[13px] transition-colors flex items-center justify-center"
                    [ngClass]="currentPage === totalPages ? 'border-gray-200 text-gray-300 cursor-not-allowed' : 'border-gray-300 text-gray-600 hover:bg-gray-50 cursor-pointer'">
                <i class="fa-solid fa-chevron-right text-xs"></i>
            </button>
        </div>

    </div>



    <!-- <div *ngIf="serverCreating" style="position:fixed;width: 100%;padding: 16px;background-color: rgb(131, 131, 255);text-align: center; z-index: 1000000000; display: flex; align-items: center;justify-content: center;">
        Creating server {{serverCreating.name}}, please wait 5 to 8 minutes...
        <ion-spinner style="margin-left: 20px;width: 18px;padding-top: 20px;"></ion-spinner>
    </div>
    <div *ngIf="loading"
        style="position:fixed;width: 100%;height: 100%;background-color: rgba(240, 248, 255, 0.685);display: flex;justify-content: center;align-items: center;z-index: 100000;">
        <ion-spinner></ion-spinner>
    </div>
    
    <div class="instances">
        <div style="font-size: 30px;font-weight: bold;margin-bottom: 15px; margin-top: 16px;">{{ 'ALL_YOUR_TOTALUM_PROJECTS' | translate }}</div>

        <div class="b-instances-list">
            <div class="instances-list-header">
                <div class="instances-list-header-title">{{ 'FREE_PROJECTS' | translate }}</div>
                <div class="instances-list-header-button">
                    <ion-button class="button-primary" (click)="openCreateInstanceModal({plan:'free'})" mode="ios">{{"CREATE_FREE_PROJECT" | translate}}</ion-button>
                </div>
            </div>
            
            <div class="instances-list-content">
                
                <ng-container *ngIf="!freeInstances || freeInstances?.length === 0">
                    <span>
                        You don't have any free Totalum projects created yet, click on "Create new" to start.
                    </span>
                </ng-container>

                <div class="instances-list-content-instances" *ngIf="freeInstances && freeInstances.length > 0">
                    <app-instance-box   *ngFor="let instance of freeInstances"
                    (onOpenInstance)="openTotalumInstance(instance)" [instance]="instance"
                    (onInteractInstance)="onInteractInstance($event, instance)"></app-instance-box>
                </div>
            </div>
        </div>

        <div style="position: relative;">

            <div class="no-pay-overlay" *ngIf="(userData && !userData?.stripeClientId)">
                <div> For continue to a higher plan you need to enter a valid card</div>
                <br>
                <ion-button (click)="goToBilling()" class="button-success" mode="ios">Add Payment Method</ion-button>
            </div>
        <div  [ngStyle]="(userData && !userData?.stripeClientId) ? {'filter': 'blur(3px)', 'pointer-events': 'none', 'user-select': 'none'} : {}">
            
        <div class="b-instances-list">
            <div class="instances-list-header">
                <div class="instances-list-header-title">{{ 'BASIC_PROJECTS' | translate }}</div>
                <div class="instances-list-header-button">
                    <ion-button class="button-primary" (click)="openCreateInstanceModal({plan:'shared'})" mode="ios">{{ 'CREATE_BASIC_PROJECT' | translate }}</ion-button>
                </div>
            </div>

            
            <div class="instances-list-content">
                <ng-container *ngIf="!sharedInstances || sharedInstances?.length === 0">
                    <span>
                        You don't have any shared instances yet, click on "Create new" to start.
                    </span>
                </ng-container>

                <div class="instances-list-content-instances" *ngIf="sharedInstances && sharedInstances.length > 0">
                    <app-instance-box   *ngFor="let instance of sharedInstances"
                    (onOpenInstance)="openTotalumInstance(instance)" [instance]="instance"
                    (onInteractInstance)="onInteractInstance($event,instance)"></app-instance-box>
                </div>
            </div>
        </div>

        <div class="b-instances-list">
            <div class="instances-list-header">
                <div class="instances-list-header-title"> {{ 'PROFESSIONAL_PROJECTS' | translate }}</div>
                <div class="instances-list-header-button">
                    <ion-button class="button-primary" (click)="openCreateInstanceModal({plan:'profesional'})" mode="ios">{{ 'CREATE_PROFESSIONAL_PROJECT' | translate }}</ion-button>
                </div>
            </div>

            
            <div class="instances-list-content">
                <ng-container *ngIf="!profesionalInstances || profesionalInstances?.length === 0">
                    <span>
                        {{ 'NO_PROFESSIONAL_INSTANCES_YET_CLICK_TO_CREATE' | translate }}
                    </span>
                </ng-container>

                <div class="instances-list-content-instances" *ngIf="profesionalInstances && profesionalInstances.length > 0">
                    <app-instance-box   *ngFor="let instance of profesionalInstances"
                    (onOpenInstance)="openTotalumInstance(instance)" [instance]="instance"
                    (onInteractInstance)="onInteractInstance($event,instance)"></app-instance-box>
                </div>
            </div>
        </div>

        <div class="b-instances-list">
            <div class="instances-list-header">
                <div class="instances-list-header-title">{{ 'DEDICATED_SERVERS' | translate }}</div>
                <div class="instances-list-header-button">
                    <ion-button class="button-tertiary" (click)="openCreateServerModal()" mode="ios">{{ 'CREATE_SERVER' | translate }}</ion-button>
                </div>
            </div>

            <div class="instances-list-content">
                <ng-container *ngIf="!servers || servers?.length === 0">
                    <span>
                        {{ 'YOU_HAVE_NO_DEDICATED_SERVERS_YET_CLICK_TO_CREATE' | translate }}
                    </span>
                </ng-container>

                <div class="server" *ngFor="let server of servers">
                    <div class="instances-list-header">
                        <div class="instances-list-header-title">{{ 'SERVER' | translate }} {{server?.name}}</div>
                        <div class="instances-list-header-button">
                            <ion-button (click)="openCreateInstanceModal({serverName: server?.name})" class="button-primary"  mode="ios">{{ 'CREATE_PROJECT_IN_THIS_SERVER' | translate }}</ion-button>
                            <ion-button mode="ios" class="button-secondary" (click)="onShowServerPopoverMenu($event, server)">
                                <ion-icon slot="start" name="create-outline"></ion-icon>
                                
                                {{ 'EDIT' | translate }}
                            </ion-button>
                        </div>
                    </div>
                    <div class="server-content">
                        <ng-container *ngIf="!server?.totalumInstancesPupulated || server.totalumInstancesPupulated?.length === 0">
                            <span>
                                {{ 'NO_INSTANCES_IN_SERVER_CLICK_TO_CREATE_FOR_START' | translate }}
                            </span>
                        </ng-container>

                        <ng-container *ngIf="$any(server?.totalumInstancesPupulated)?.length > 0">
                            <app-instance-box *ngFor="let instance of server?.totalumInstancesPupulated"
                            (onOpenInstance)="openTotalumInstance(instance)"
                            (onInteractInstance)="onInteractInstance($event, instance, server.name)"
                            [instance]="instance"
                            
                            ></app-instance-box>
                        </ng-container>
                    </div>
                </div>
            </div>
        </div>

    </div>
    </div>
        <br><br><br><br><br>

    </div> -->
</ion-content>